'use client';

import { useForYouList } from '@/features/for-you/hooks/useForYouList';
import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { categoryService } from '../services/categoryService';
import type { IPagination } from '../types/common';

export const useCategories = () => {
	return useQuery({
		queryKey: ['categories'],
		queryFn: () => categoryService.getCategories(),
	});
};

export const usePopularCategories = (params: IPagination) => {
	return useQuery({
		queryKey: ['categories', 'popular', params],
		queryFn: () => categoryService.getPopularCategories(params),
	});
};

export const useCategoryInfo = (category: string) => {
	return useQuery({
		queryKey: ['categories', category],
		queryFn: () => categoryService.getCategoryInfo(category),
		enabled: !!category,
	});
};

export const useRelatedCategories = (category: string) => {
	return useQuery({
		queryKey: ['categories', category, 'related'],
		queryFn: () => categoryService.getRelatedCategories(category),
		enabled: !!category,
	});
};

export const useCategoryFollowers = (category: string, params: IPagination) => {
	return useQuery({
		queryKey: ['categories', category, 'followers', params],
		queryFn: () => categoryService.getCategoryFollowers(category, params),
		enabled: !!category,
	});
};

export const useCategoryConversations = (
	category: string,
	params: IPagination,
) => {
	return useQuery({
		queryKey: ['categories', category, 'conversations', params],
		queryFn: () => categoryService.getCategoryConversations(category, params),
		enabled: !!category,
	});
};

export const useCategoryFollow = (category: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => categoryService.followCategory(category),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['categories', category] });
			queryClient.invalidateQueries({ queryKey: ['categories', 'popular'] });
		},
	});
};

export const useCategoryUnfollow = (category: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => categoryService.unfollowCategory(category),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['categories', category] });
			queryClient.invalidateQueries({ queryKey: ['categories', 'popular'] });
		},
	});
};

export const useCategoryReport = (category: string) => {
	return useMutation({
		mutationFn: () => categoryService.reportCategory(category),
	});
};

export const useCategoryDetail = (category: string) => {
	return useQuery({
		queryKey: ['category-detail', category],
		queryFn: () => categoryService.getCategoryInfo(category),
	});
};

export const useFollowCategory = () => {
	return useMutation({
		mutationFn: (category: string) => categoryService.followCategory(category),
	});
};

export const useUnfollowCategory = () => {
	return useMutation({
		mutationFn: (category: string) =>
			categoryService.unfollowCategory(category),
	});
};

export const useCategoryPosts = (category: string) =>
	useForYouList(({ page, size }: IPagination) => {
		return useInfiniteQuery({
			queryKey: ['category-posts', category, page, size],
			queryFn: () =>
				categoryService.getCategoryConversations(category, { page, size }),
			getNextPageParam: (lastPage) =>
				lastPage.page.last ? undefined : lastPage.page.number + 1,
			initialPageParam: 1,
			enabled: !!size,
		});
	});
